import { ref, provide, computed, watch } from "vue";
import { useStore } from "vuex";
import {
  options,
  columns,
  archivedDevicesColumns,
} from "@/helpers/deviceTableHelper";
import { groupBy } from "@/helpers/sharedHelper";
import $ from "jquery";

export default function useDevicesTable(tableType) {
  const store = useStore();

  const loading = computed(() => store.getters["devices/isLoading"]);
  const deviceQuery = computed(() => store.getters["devices/getDeviceQuery"]);
  const pageLength = computed(() => deviceQuery.value.length);
  const totalRecords = computed(() => store.getters["devices/getRecordsTotal"]);
  const recordsFiltered = computed(
    () => store.getters["devices/getRecordsFiltered"]
  );
  const tableData = computed(() => store.getters["devices/getDevices"]);

  let devicesFilter = deviceQuery.value;

  const fetchDevices = async () => {
    store.dispatch("devices/setDeviceQuery", devicesFilter);

    if (tableType === "archivedDevices") {
      await store.dispatch("devices/fetchArchivedDevices");
    } else {
      await store.dispatch("devices/fetchDevices");
    }

    selectedDevices.value = [];
  };

  const tableOptions = {
    ...options,
    pageLength: pageLength.value,
    deferLoading: totalRecords.value,
    ajax: (data, callback) => {
      if (!loading.value) {
        const validData = Array.isArray(tableData.value) ? tableData.value : [];
        const validRecordsTotal = totalRecords.value || validData.length || 0;
        const validRecordsFiltered =
          recordsFiltered.value || validData.length || 0;

        callback({
          draw: data.draw,
          recordsTotal: validRecordsTotal,
          recordsFiltered: validRecordsFiltered,
          data: validData,
        });
      }
    },
    buttons: [
      function () {
        return {
          text: "Select All",
          action: function (_, dt) {
            dt.rows().select();
          },
          init: function (api, node) {
            $(node).attr("id", "select-all-button");

            function updateButtonState() {
              const shouldBeEnabled =
                (tableType === "archivedDevices" &&
                  deviceQuery.value.courses != null) ||
                tableType === "totalDevices";

              $(node).prop("disabled", !shouldBeEnabled);

              if (shouldBeEnabled) {
                $(node).removeClass("disabled");
              } else {
                $(node).addClass("disabled");
              }
            }

            updateButtonState();

            watch(
              () => deviceQuery.value.courses,
              () => {
                updateButtonState();
              }
            );
          },
        };
      },
      {
        text: "Deselect All",
        action: function (_, dt) {
          dt.rows().deselect();
        },
      },
      function () {
        if (tableType !== "archivedDevices") {
          return {
            extend: "csvHtml5",
            text: "CSV",
            title: "Central",
            exportOptions: {
              columns: [
                0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                18,
              ],
            },
            customizeData: (data) => {
              const dt = this;
              const selectedRows = dt.rows({ selected: true }).data().toArray();
              if (selectedRows.length > 0) {
                data.body = selectedRows.map((row) => Object.values(row));
              } else {
                const allRows = dt.rows().data().toArray();
                data.body = allRows.map((row) => Object.values(row));
              }
            },
          };
        }
        return null;
      },
    ],
  };
  const selectedDevices = ref([]);

  const selectedDevicesByCourseName = computed(() => {
    return groupBy(selectedDevices.value, "course_name");
  });

  provide("selectedDevices", selectedDevices);

  provide("selectedDevicesByCourseName", selectedDevicesByCourseName);

  const onPageChange = ({ event }) => {
    const info = event.dt.page.info();
    devicesFilter.start = info.page + info.length;
    fetchDevices();
  };

  const handleSearch = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.search === "function") {
      const searchValue = dataTable.search();
      if (deviceQuery.value.search !== searchValue) {
        devicesFilter.start = 0;
        devicesFilter.search = searchValue;
        fetchDevices();
      }
    }
  };

  const handleSortChange = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.order === "function") {
      const order = dataTable.order();
      if (!order || order.length === 0) {
        if (devicesFilter.orderBy !== "id" || devicesFilter.dir !== "asc") {
          devicesFilter.orderBy = "id";
          devicesFilter.dir = "asc";
          fetchDevices();
        }
        return;
      }

      const direction = order[0][1];
      const column = direction === "" ? "id" : columns[order[0][0]].data;

      const normalizedDirection = direction === "" ? "asc" : direction;

      if (
        devicesFilter.orderBy !== column ||
        devicesFilter.dir !== normalizedDirection
      ) {
        devicesFilter.orderBy = column;
        devicesFilter.dir = normalizedDirection;

        fetchDevices();
      }
    }
  };

  const handleLengthChange = ({ dt }) => {
    const newLength = dt._iDisplayLength;
    devicesFilter.length = newLength;
    fetchDevices();
  };

  const onSelect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedDevices.value = selectedRows.toArray();
  };

  const onDeselect = ({ dt }) => {
    const deSelectedRows = dt.rows({ selected: true }).data();
    selectedDevices.value = deSelectedRows.toArray();
  };

  const resetTable = () => {
    store.commit("devices/RESET_DEVICE_QUERY");
    store.commit("devicesFilterOptions/RESET_FILTER_OPTIONS");
  };

  return {
    tableData,
    columns,
    archivedDevicesColumns,
    tableOptions,
    onPageChange,
    handleSearch,
    handleSortChange,
    handleLengthChange,
    onSelect,
    onDeselect,
    resetTable,
  };
}
